import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useGetPaymentElectionQuery } from '@/modules/issuer-dashboard/queries/charts.ts';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { ChartEmptyState, InfoCard, ProgressChart, ProgressChartItem, Select } from 'ui';
import { goodFormatNumber } from 'ui/utils';

export const HolderPaymentElectionChart = () => {
  const { data: dataPaymentElection, isLoading, isError } = useGetPaymentElectionQuery();

  const [params] = useSearchParams();
  const currentRegisterId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');
  const { data: classesData, isLoading: isLoadingClasses } = useIssuerRegisterClassListQuery(
    {},
    currentRegisterId,
  );
  const [registerClassFilter, setRegisterClassFilter] = useState<number | null>();

  const classesOptions = useMemo(
    () => [
      ...(classesData?.registers || []).map(({ id, class_name }) => ({
        label: class_name,
        value: id,
      })),
    ],
    [classesData?.registers],
  );

  const colors = ['#1E6091', '#48CAE4', '#56C596', '#F4C430', '#B276E2'];

  const items = useMemo(() => {
    const result: (ProgressChartItem & { percentage: number })[] = [];

    const resultByClass = dataPaymentElection?.data?.find(
      item => item.register_class_id === (registerClassFilter || classesOptions?.[0]?.value),
    );

    if (!resultByClass) return result;

    result.push({
      name: 'DRP',
      value: resultByClass?.drp?.total || 0,
      percentage: resultByClass?.drp?.percentage || 0,
      valueName: ' Investors',
    });
    result.push({
      name: 'Direct Credit',
      value: resultByClass?.direct_credit?.total || 0,
      percentage: resultByClass?.direct_credit?.percentage || 0,
      valueName: ' Investors',
    });
    // result.push({
    //   name: 'None',
    //   value: dataPaymentElection?.data?.[0]?.none?.total || 0,
    //   percentage: dataPaymentElection?.data?.[0]?.none?.percentage || 0,
    //   valueName: ' Investors',
    // });

    return result;
  }, [classesOptions, dataPaymentElection?.data, registerClassFilter]);

  return (
    <InfoCard
      title="Holder Payment Election"
      classes={{ content: 'px-0 pb-0' }}
      headerAction={
        <div>
          <Select
            label=""
            isLoading={isLoadingClasses}
            placeholder="Classes"
            className="min-w-[190px] w-full"
            onChange={setRegisterClassFilter}
            value={registerClassFilter || classesOptions?.[0]?.value}
            options={classesOptions}
            withBorder={true}
          />
        </div>
      }
    >
      <div className="flex flex-col">
        {(!items?.length || isLoading) && (
          <div className="flex justify-center items-center font-medium text-lg px-6 py-6">
            <ChartEmptyState
              isError={isError}
              isLoading={isLoading}
              loadingCount={4}
              notFoundCount={4}
            />
          </div>
        )}
        {!isLoading && items?.length > 0 && (
          <>
            <div className="relative px-6">
              <ProgressChart items={items} colors={colors} />
            </div>
            <div className="w-full flex flex-col border-t border-t-gray-200 px-6 text-sm leading-[22px] font-[Inter]">
              {items.map((item, i, all) => (
                <div
                  key={item.name}
                  className={classNames('w-full flex gap-4 items-center py-3', {
                    'border-b border-b-gray-200': i < all.length - 1,
                  })}
                >
                  <div className="flex items-center gap-3 w-[30%] sm:min-w-[150px] font-[500]">
                    <span
                      className="block w-[6px] h-[6px] min-h-[6px] min-w-[6px] rounded-full"
                      style={{ background: colors[i] }}
                    ></span>
                    <span>{item.name}</span>
                  </div>
                  <div className="w-[30%] sm:min-w-[150px]">
                    {item.value} {item.valueName}
                  </div>
                  <div className="w-[30%] sm:min-w-[150px]">
                    {goodFormatNumber(item.percentage)}%
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </InfoCard>
  );
};
